import { Input } from '@components/core-ui/form-elements/Input';
import { Select } from '@components/core-ui/form-elements/Select';
import { useAsrProxyConsoleContext } from '../../../../providers/AsrProxyConsoleProvider';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { useAPI } from '@providers/useAPI';
import { useQueryClient } from '@tanstack/react-query';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { useState } from 'react';
import { createFetchAsrTranscriptionQueryFn } from '@pages/Admin/AsrProxyConsolePage/api/fetchAsrTranscription';
import { createFetchAsrProviderTranscriptionQueryFn } from '@pages/Admin/AsrProxyConsolePage/api/fetchAsrProviderTranscription';
import type { TProps } from './types/TProps';

export const LookupPanel = ({
  id,
  provider,
  providerTranscriptId,
  onIdChange,
  onProviderChange,
  onProviderTranscriptIdChange,
  onResult,
  onError,
}: TProps) => {
  const { call } = useAPI();
  const { adapters } = useAsrProxyConsoleContext();
  const hasSelectedProvider = adapters.includes(provider);
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const fetchTranscript = async (byProvider = false) => {
    const value = (byProvider ? providerTranscriptId : id).trim();
    if (!value || (byProvider && !hasSelectedProvider)) {
      return;
    }
    setBusy(true);
    try {
      const result = byProvider
        ? await queryClient.fetchQuery({
            queryKey: asrProxyKeys.providerTranscription(provider.trim(), value),
            queryFn: createFetchAsrProviderTranscriptionQueryFn(call),
            staleTime: 0,
          })
        : await queryClient.fetchQuery({
            queryKey: asrProxyKeys.transcription(value),
            queryFn: createFetchAsrTranscriptionQueryFn(call),
            staleTime: 0,
          });
      if (result?.id && result?.status) {
        onResult(result);
      } else {
        onError(
          result?.status ? `No transcript for ${value} (status ${result.status}).` : `No transcript for ${value}.`,
        );
      }
    } catch (lookupError) {
      onError(lookupError instanceof Error ? lookupError.message : String(lookupError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-[10px] bg-surface p-5 shadow-[0_8px_10px_var(--color-shadow)] xl:min-h-0 xl:overflow-y-auto">
      <div className="mb-4">
        <h2 className="text-lg font-medium text-foreground">Find transcript</h2>
        <p className="mt-1 text-sm text-foreground-muted">
          Find a proxy job by its proxy ID or provider transcript ID. Direct AssemblyAI jobs are not stored in the
          proxy.
        </p>
      </div>
      <Input
        name="playground-proxy-id"
        label="Proxy ID"
        value={id}
        onChange={(event) => onIdChange(event.target.value)}
        onKeyUp={(event) => {
          if (event.key === 'Enter') {
            fetchTranscript();
          }
        }}
      />
      <Button className="mt-4 !w-auto" disabled={busy || !id.trim()} onClick={() => fetchTranscript()}>
        {busy ? 'Fetching…' : 'Find by proxy ID'}
      </Button>
      <div className="mt-5 grid gap-3">
        <Select
          name="playground-provider"
          label="Provider"
          value={hasSelectedProvider ? provider : ''}
          options={adapters.map((adapter) => ({ label: adapter, value: adapter }))}
          onChange={(_name, value) => onProviderChange(value)}
          disabled={busy || adapters.length === 0}
        />
        {adapters.length === 0 ? (
          <p role="status" className="text-sm text-foreground-muted">
            Provider list is unavailable. You can still search by proxy ID.
          </p>
        ) : null}
        <Input
          name="playground-provider-transcript-id"
          label="Provider Transcript ID"
          value={providerTranscriptId}
          onChange={(event) => onProviderTranscriptIdChange(event.target.value)}
          onKeyUp={(event) => {
            if (event.key === 'Enter') {
              fetchTranscript(true);
            }
          }}
        />
        <Button
          className="!w-auto"
          disabled={busy || !hasSelectedProvider || !providerTranscriptId.trim()}
          onClick={() => fetchTranscript(true)}
        >
          Find by provider ID
        </Button>
      </div>
    </section>
  );
};
